import moment from "moment";
import React, { useState, Dispatch as ReactDispatch, SetStateAction } from "react";
import { connect } from "react-redux";
import { ITabReport } from "redux/app/logs/@types";
import { IStore } from "redux/store";
import ToolsModal from "./ToolsModal";
import axios from "axios";
import { toast } from "react-hot-toast";
import { classNames } from "utils/classNames";
import ReportTimelineRow from "./ReportTimelineRow";
import { rootRoutes } from "routes";

interface IReportRowProps {
	report: ITabReport;
	key: number;
	onClick: (report: ITabReport) => void;
	setErrorDetailsModalOpen: ReactDispatch<SetStateAction<boolean>>;
	setErrorDetails: ReactDispatch<SetStateAction<any>>;
	indexNumber: number;
	isUserThirdParty: boolean;
	isUserAdmin: boolean;
	isUserAllowedToUseCNRTool: boolean;
	vesselName: string;
	vesselImo: string;
	refreshReports: () => void;
	vesselId?: string;
}

export const getTitle = (report: ITabReport) => {
	return "CNR";
};

export const getSelectedDate = (report: ITabReport) => {
	if (report?.selectedDate?.includes("T")) {
		if (report?.intlDateLineRecord) {
			return `${report?.selectedDate?.split("T")?.[0]} (IDL Repeat)`;
		}
		return `${report?.selectedDate?.split("T")?.[0]}`;
	}

	return "";
};

export const getStatus = (report: ITabReport) => {
	if (report?.doc_status) {
		return `${report?.doc_status?.charAt(0)}${report?.doc_status
			?.slice(1)
			.toLowerCase()}`;
	}
	return "";
};

// Helper function to get validation status for a report item
export const getValidationStatus = (item: any, isItADerivedReport?: boolean) => {
	// Embedded cargo reports don't have isValid flag and should not be restricted
	if (isItADerivedReport && item?.report_type === "CARGO") {
		return { isValid: "exempt", icon: "➖", text: "Not Applicable" };
	}
	
	if (item?.isValid === true) {
		return { isValid: true, icon: "✅", text: "Valid" };
	} else if (item?.isValid === false) {
		return { isValid: false, icon: "❌", text: "Invalid" };
	} else {
		return { isValid: undefined, icon: "⚠️", text: "Not Set" };
	}
};

const ReportRow: React.FC<IReportRowProps> = (props) => {
	const {
		report,
		isUserThirdParty,
		isUserAdmin,
		isUserAllowedToUseCNRTool,
		vesselName,
		vesselImo,
		vesselId,
	} = props;
	const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const isImosDeferred = report?.imosApiStatus === "DEFERRED";
	const deferredImosDetails = {
		formID: null,
		success: false,
		formStatus: "DEFERRED",
		formErrors: null,
		message: "Deferred during SG/London office peak hours",
	};

	const handleToolAction = async (actionType: string, data: any) => {
		setIsLoading(true);
		const loadingToast = toast.loading(`Processing ${actionType.toLowerCase()} action...`);

		try {
			// Make the API call using axios
			const response = await axios.post('/api/cnractions', {
				action: actionType,
				reportId: report._id,
				data: data
			});

			// if the response is successful, close the modal and refresh the page
			if (response.status === 200) {
				setIsToolsModalOpen(false);
				toast.success(`Successfully completed ${actionType.toLowerCase()} action`, {
					id: loadingToast,
					duration: 3000
				});
				props.refreshReports();
			} else {
				toast.error(`Failed to complete ${actionType.toLowerCase()} action`, {
					id: loadingToast,
					duration: 3000
				});
			}
		} catch (error: any) {
			console.error("Error handling tool action:", error);
			const errorMessage = error?.response?.data?.message || error?.message || "Unknown error occurred";
			toast.error(`Action failed: ${errorMessage}`, {
				id: loadingToast,
				duration: 5000
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Legacy has been replaced by latest delayoff hire.
	const checkIfThereIsLatestDelayOffHire =
		report?.standaloneForm?.delayOffHireNotice &&
		"delayOffHireNotice" in report?.standaloneForm?.delayOffHireNotice &&
		Array.isArray(
			report?.standaloneForm?.delayOffHireNotice["delayOffHireNotice"]
		);
	const checkIfThereIsLegacyDelayOffHire =
		report?.standaloneForm?.delayOffHireNotice &&
		!("delayOffHireNotice" in report?.standaloneForm?.delayOffHireNotice);
	// If none of the above true, that means user hasn't filled the delayoffhire. We need to create two separate conditions because there can be a situation of user has not created delay form.

	const delayOffHireNotice = checkIfThereIsLatestDelayOffHire
		? report?.standaloneForm?.delayOffHireNotice["delayOffHireNotice"]
		: checkIfThereIsLegacyDelayOffHire
		? [report?.standaloneForm?.delayOffHireNotice]
		: null;

	// Check if any timeline items are invalid
	// Note: Embedded cargo reports (part of departure reports) don't have isValid flag and should be excluded from validation
	const hasInvalidItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === false;
		}
		return item?.isValid === false;
	});
	
	const hasUnsetItems = report?.timeline?.some((item: any) => {
		// Skip validation for embedded cargo reports
		if (item?.report_type === "DEPARTURE" && item?.cargoReport && Array.isArray(item?.cargoReport)) {
			// This is a departure report with embedded cargo reports, check only the departure report itself
			return item?.isValid === undefined || item?.isValid === null;
		}
		return item?.isValid === undefined || item?.isValid === null;
	});
	
	// Determine row styling based on validation status
	const getRowClassName = () => {
		const baseClass = "my-1 border hover:cursor-pointer";
		if (hasInvalidItems) {
			return `${baseClass} bg-red-50 hover:bg-red-100`;
		} else if (hasUnsetItems) {
			return `${baseClass} bg-yellow-50 hover:bg-yellow-100`;
		} else {
			return `${baseClass} bg-white hover:bg-gray-200`;
		}
	};

	return (
		<tr
			key={report._id}
			className={getRowClassName()}
		>
			<td className="text-center text-gray-400 px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
				{props.indexNumber}
			</td>
			<td
				className="font-mono text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[6px] w-[110px] md:text-xs lg:text-sm"
				title={moment(report.createdAt).format("DD-MM-YYYY hh:mm a")}
			>
				{moment(report.createdAt).utc().format("DD-MM-YYYY hh:mm a")}
			</td>
			<td className="font-mono text-center px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
				{getSelectedDate(report)}
			</td>
			<td className="font-mono text-center px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
				<table
					className="mx-auto table-auto reportTimelineTable lg:table-fixed"
				>
					<tbody>
					{report?.timeline?.map((item, index) => {
							const checkIfItemIsDepartureReport =
								item?.report_type === "DEPARTURE";
							const checkIfDepRepHasCargoReport =
								checkIfItemIsDepartureReport &&
								item?.cargoReport &&
								Array.isArray(item?.cargoReport);

							return (
								<React.Fragment key={index}>
									<ReportTimelineRow
										key={index}
										isUserThirdParty={isUserThirdParty}
										report={report}
										item={item}
										setErrorDetails={props.setErrorDetails}
										setErrorDetailsModalOpen={props.setErrorDetailsModalOpen}
										index={index}
										timeline={report?.timeline}
									/>
									{checkIfDepRepHasCargoReport  ? (
										// if the item is departure report and has cargo report, then show the cargo report in a new row
										<>
											{item?.cargoReport?.map(
												(cargoItem: any, cargoIndex: number) => {
													// Check if unwanted records are existing.
													if(cargoItem && Object.keys(cargoItem).length === 0) return null;
													cargoItem = {
														...cargoItem,
														// There is no to date,time,zd in cargo report
														to_local_date: cargoItem?.to_cast_off_local_date,
														to_local_time: cargoItem?.to_cast_off_local_time,
														to_zd: cargoItem?.to_cast_off_zd,
													};
													return (
														<ReportTimelineRow
															key={index.toString() + cargoIndex}
															isUserThirdParty={isUserThirdParty}
															report={report}
															item={cargoItem}
															setErrorDetails={props.setErrorDetails}
															setErrorDetailsModalOpen={
																props.setErrorDetailsModalOpen
															}
															index={index}
															isItADerivedReport={true}
															derivedReportHint="Is a part of Departure Report"
														/>
													);
												}
											)}
										</>
									) : null}
								</React.Fragment>
							);
						})}

						{/* also check if there is standaloneForm available in which check for statementOfFacts and delayOffHireNotice forms  */}
						{/* example {standaloneForm: {statementOfFacts:{},delayOffHireNotice:{} }} */}

						{report?.standaloneForm?.statementOfFacts && (
							<tr className="h-full">
								<td className="border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[86px] xl:w-[90px]">
									SOF
								</td>
								<td className=" border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[100px] xl:w-[230px]">
									Statement of Facts
								</td>
								<td className=" text-gray-500 lg:text-[12px]  xl:text-sm border border-gray-200 py-1 px-1 text-[6px] md:text-xs">
									{
										report?.standaloneForm?.statementOfFacts
											?.sof_eosp_date
									}{" "}
									{
										report?.standaloneForm?.statementOfFacts
											?.sof_eosp_time
									}{" "}
									{
										report?.standaloneForm?.statementOfFacts
											?.sof_eosp_zd
									}
								</td>
								<td
									className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs "
									title={`Not Applicable`}
								>
									{/* INFO: we don't SOF to Signal, so show not applicable emoji */}
									➖
								</td>
								<td
									className="text-center cursor-pointer  lg:text-[12px] w-8  xl:text-sm border border-gray-200 py-1 px-1 text-[8px] md:text-xs"
									title={`${report?.standaloneForm?.statementOfFacts
											?.imosApiResponse
											? `Last Processed at ${moment(
												report?.standaloneForm
													?.statementOfFacts
													?.imosApiResponse
													?.lastProcessedDate
											).format("DD-MM-YYYY hh:mm a")}`
											: isImosDeferred
											? "Deferred during SG/London office peak hours"
											: ""
										}`}
									// on click of IMOS status, it should show the IMOS API response in a dialog/modal
									onClick={() => {
										// find the erroneous report in the report.timeline by searching for the imosApiResponse
										// and show the response in a dialog/modal
										const erroneousReport =
											report?.standaloneForm
												?.statementOfFacts;

										if (erroneousReport) {
											props.setErrorDetails({
												reportName:
													"Statement of Facts",
												errorDetails:
													erroneousReport?.imosApiResponse ||
													(isImosDeferred ? deferredImosDetails : undefined),
												lastProcessedDate:
													report?.imosApiLastProcessedDate,
												reportType: "IMOS",
												docId: report._id,
												vesselId: report.vesselId,
												doc_status: report.doc_status,
											});
											props.setErrorDetailsModalOpen(
												true
											);
										} else {
											console.log(
												"IMOS API Response not found in the report"
											);
										}
									}}
								>
									{report?.standaloneForm?.statementOfFacts
										?.imosApiResponse
										? report?.standaloneForm
											?.statementOfFacts
											?.imosApiResponse?.success
											? "✅"
											: "❌"
										: isImosDeferred
										? "⏸️"
										: "⏳"}
								</td>
								{/* Validation Status Column */}
								<td
											className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs"
											title={`Validation Status: ${getValidationStatus({ ...report?.standaloneForm?.statementOfFacts, isValid: report?.standaloneForm?.statementOfFacts?.isValid }, false).text}`}
										>
											{getValidationStatus({ ...report?.standaloneForm?.statementOfFacts, isValid: report?.standaloneForm?.statementOfFacts?.isValid }, false).icon}
										</td>
							</tr>
						)}

						{delayOffHireNotice && (
							delayOffHireNotice?.map((item:any,index:number) => {

								const {delayOffhireNotice_start_date,delayOffhireNotice_start_time,delayOffhireNotice_start_offset} = item;

								return <tr className="h-full" key={index}>
								<td className="border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[86px] xl:w-[90px]">
									DELAY
								</td>
								<td className=" border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[100px] xl:w-[230px]">
									Delay Offhire Notice
								</td>
								<td className=" text-gray-500 lg:text-[12px]  xl:text-sm border border-gray-200 py-1 px-1 text-[6px] md:text-xs">
									{
										delayOffhireNotice_start_date
									}{" "}
									{
										delayOffhireNotice_start_time
									}{" "}
									{
										delayOffhireNotice_start_offset
									}
								</td>
								<td
									className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs "
									title={`Not Applicable`}
								>
									{/* INFO: we don't send Delay offhire to Signal, so show not applicable emoji */}
									➖
								</td>
								<td
									className="text-center cursor-pointer  lg:text-[12px] w-8  xl:text-sm border border-gray-200 py-1 px-1 text-[8px] md:text-xs"
									title={`${report?.standaloneForm
											?.delayOffHireNotice
											?.imosApiResponse
											? `Last Processed at ${moment(
												report?.standaloneForm
													?.delayOffHireNotice
													?.imosApiResponse
													?.lastProcessedDate
											).format("DD-MM-YYYY hh:mm a")}`
											: isImosDeferred
											? "Deferred during SG/London office peak hours"
											: ""
										}`}
									// on click of IMOS status, it should show the IMOS API response in a dialog/modal
									onClick={() => {
										// find the erroneous report in the report.timeline by searching for the imosApiResponse
										// and show the response in a dialog/modal
										const erroneousReport =
											report?.standaloneForm
												?.delayOffHireNotice;

										if (erroneousReport) {
											props.setErrorDetails({
												reportName:
													"Delay Offhire Notice",
												errorDetails:
													erroneousReport?.imosApiResponse ||
													(isImosDeferred ? deferredImosDetails : undefined),
												lastProcessedDate:
													report?.imosApiLastProcessedDate,
												reportType: "IMOS",
												docId: report._id,
												vesselId: report.vesselId,
												doc_status: report.doc_status,
											});
											props.setErrorDetailsModalOpen(
												true
											);
										} else {
											console.log(
												"IMOS API Response not found in the report"
											);
										}
									}}
								>
									{report?.standaloneForm?.delayOffHireNotice
										?.imosApiResponse
										? report?.standaloneForm
											?.delayOffHireNotice
											?.imosApiResponse?.success
											? "✅"
											: "❌"
										: isImosDeferred
										? "⏸️"
										: "⏳"}
								</td>
								{/* Validation Status Column */}
										<td
											className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs"
											title={`Validation Status: ${getValidationStatus({ ...item, isValid: report?.standaloneForm?.delayOffHireNotice?.isValid }, false).text}`}
										>
											{getValidationStatus({ ...item, isValid: report?.standaloneForm?.delayOffHireNotice?.isValid }, false).icon}
										</td>
							</tr>
							})
						)}

						{report?.standaloneForm?.bunker &&
						"bunkerReport" in report?.standaloneForm?.bunker &&
						Array.isArray(report?.standaloneForm?.bunker["bunkerReport"])
							? report?.standaloneForm?.bunker["bunkerReport"]?.map(
									(item: any, index: number) => {
										// There is no to date,time,zd in bunker report
										const { bunker_delivery_local_date, bunker_delivery_local_time, bunker_delivery_zd } = item;

										return (
											<tr className="h-full">
												<td className="border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[86px] xl:w-[90px]">
													<div className="flex items-center">
														<span className="bp3-icon bp3-icon-selection mr-1" />	
														<span>BUNKER</span>
													</div>
												</td>
												<td className=" border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[100px] xl:w-[230px]">
													BUNKER
												</td>
												<td className=" text-gray-500 lg:text-[12px]  xl:text-sm border border-gray-200 py-1 px-1 text-[6px] md:text-xs">
													{bunker_delivery_local_date} {bunker_delivery_local_time} {bunker_delivery_zd}
												</td>
												<td
													className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs "
													title={`${
														report?.signalApiLastProcessedDate
															? `Last Processed at ${moment(
																	report?.signalApiLastProcessedDate
																).format("DD-MM-YYYY hh:mm a")}`
															: ""
													}`}
													onClick={() => {
														// find the erroneous report in the report.timeline by searching for the signalApiResponse
														// and show the response in a dialog/modal
														const erroneousReport = item;
								
														if (erroneousReport) {
															props.setErrorDetails({
																reportName: erroneousReport?.report_type,
																errorDetails: {
																	formID: item?.signalApiResponse?.id,
																	message: item?.signalApiResponse?.detail
																		? item?.signalApiResponse?.detail
																		: JSON.stringify(item?.signalApiResponse),
								
																	success: item?.signalApiResponse?.id
																		? true
																		: item?.signalApiResponse?.detail
																		? false
																		: undefined,
																},
																lastProcessedDate: report?.signalApiLastProcessedDate,
																reportType: "SIGNAL",
																docId: report._id,
																vesselId: report.vesselId,
																doc_status: report.doc_status,
															});
															props.setErrorDetailsModalOpen(true);
														} else {
															console.log("Signal API Response not found in the report");
														}
													}}
												>
													{item?.signalApiResponse
														? item?.signalApiResponse?.id || item?.signalApiResponse === 200
															? "✅"
															: "❌"
														: 
															"⏳"}
												</td>
												<td
													className="text-center cursor-pointer  lg:text-[12px] w-8  xl:text-sm border border-gray-200 py-1 px-1 text-[8px] md:text-xs"
													title={`Not Applicable`}
												>
													{"➖"}
												</td>
												{/* Validation Status Column */}
												<td
													className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs"
													title={`Validation Status: ${getValidationStatus({ ...item, isValid: report?.standaloneForm?.bunker?.isValid }, false).text}`}
												>
													{getValidationStatus({ ...item, isValid: report?.standaloneForm?.bunker?.isValid }, false).icon}
												</td>
											</tr>
										);
									}
							  )
							: null}


						{report?.standaloneForm?.tanksROB && (
							<tr className="h-full">
								<td className="border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[86px] xl:w-[90px]">
									Tank ROBs
								</td>
								<td className=" border border-gray-200 py-1 px-1 text-[6px] md:text-xs lg:text-[12px]  xl:text-sm lg:w-[100px] xl:w-[230px]">
									Tank ROBs
								</td>
								<td className=" text-gray-500 lg:text-[12px]  xl:text-sm border border-gray-200 py-1 px-1 text-[6px] md:text-xs">
									{
										report?.standaloneForm?.tanksROB
											?.brt_report_date
									}{" "}
									{
										report?.standaloneForm?.tanksROB
											?.brt_report_time
									}{" "}
									{
										report?.standaloneForm?.tanksROB
											?.brt_report_zd
									}
								</td>
								<td
									className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs "
									title={`Not Applicable`}
								>
									{/* INFO: we don't send Tanks ROB to Signal, so show not applicable emoji */}
									➖
								</td>
								<td
									className="text-center cursor-pointer  lg:text-[12px] w-8  xl:text-sm border border-gray-200 py-1 px-1 text-[8px] md:text-xs"
									title={`Not Applicable`}
								>
										➖

								</td>
								{/* Validation Status Column */}
								<td
									className="text-center border lg:text-[12px] w-8  xl:text-sm border-gray-200 py-1 px-1 text-[8px] md:text-xs"
									title={`Validation Status: ${getValidationStatus({ ...report?.standaloneForm?.tanksROB, isValid: report?.standaloneForm?.tanksROB?.isValid }, false).text}`}
								>
									{getValidationStatus({ ...report?.standaloneForm?.tanksROB, isValid: report?.standaloneForm?.tanksROB?.isValid }, false).icon}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</td>
			
			<td className="font-medium text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[8px] md:text-xs lg:text-sm">
				{getStatus(report)}{" "}
			</td>

			<td className="text-center px-0.5 py-0.5 md:px-2 md:py-1 text-[9px] md:text-xs lg:text-sm">
				<div className="flex justify-center">
					<button
						className="p-5 py-1 font-medium text-blue-500 underline bg-transparent border-0 shadow-none ws-input hover:text-blue-700 text-[9px] md-text[12px] lg:text-sm"
						onClick={function onClick() {
							props.onClick(report);
						}}
					>
						View
					</button>
					<button
						onClick={() => {
							const cnrId = report?._id;
							if (vesselId)
								window.open(
									`${
										window.location.origin
									}${rootRoutes.children.report.children.report.url(
										cnrId
									)}`,
									"_blank"
								);
						}}
					>
						<span className={`bp3-icon text-sm bp3-icon-share`} />
					</button>
				</div>
			</td>
			{isUserAllowedToUseCNRTool && (
				<td className="font-medium text-center text-gray-700 px-0.5 py-0.5 md:px-2 md:py-1 text-[8px] md:text-xs lg:text-sm">
					<button
						onClick={(e) => {
							e.stopPropagation();
							setIsToolsModalOpen(true);
						}}
						disabled={isLoading}
						className={classNames(
							"px-3 py-1 text-sm font-medium rounded-md",
							isLoading 
								? "bg-gray-100 text-gray-400 cursor-not-allowed"
								: "text-blue-600 bg-blue-100 hover:bg-blue-200"
						)}
					>
						Tools
					</button>
					<ToolsModal
						isOpen={isToolsModalOpen}
						onClose={() => setIsToolsModalOpen(false)}
						report={report}
						vesselName={vesselName || ""}
						vesselImo={vesselImo || ""}
						onAction={handleToolAction}
						isLoading={isLoading}
					/>
				</td>
			)}
		</tr>
	);
}

function mapStateToProps(store: IStore, ownProps: IReportRowProps) {
	return {
		vesselName: ownProps.vesselName || "",
		vesselImo: ownProps.vesselImo || "",
	};
}

export default connect(mapStateToProps)(ReportRow);
